import React, { useEffect, useState } from 'react'

import { useMetric } from 'redux/hooks/Metrics'

import { Context } from 'types/GlobalUserEvents'
import { TChartClick } from 'components_new/molecules/charts/types'
import { TCommentThreadData } from 'types/GlobalComment'
import { TCustomizedMetric } from 'types/GlobalMetric'
import { TKeyMetric } from 'types/GlobalKeyMetric'

import { useCommentActions } from 'redux/actions/Comments'
import { useCommentThreads } from 'redux/hooks/Comments'
import { useIsMobile } from 'utils/hooks'
import { useMetricQueryParamsState } from 'utils/useMetricQueryParamsState'
import { useTasksFromKeyMetric } from 'redux/hooks/Tasks'

import Icon from 'components_new/atoms/Icon'

import IconButton from 'components_new/atoms/IconButton'
import SidebarDialog from 'components_new/molecules/SidebarDialog'

import KeyMetricSidebar from 'components_new/organisms/KeyMetricSidebar'
import MetricActiveFilters from 'components_new/organisms/MetricActiveFilters'
import MetricTitle from 'components_new/organisms/MetricTitle'
import MetricQueryParamsFooter from 'components_new/organisms/MetricQueryParamsFooter'
import MetricView from 'components_new/organisms/MetricView'

import { hasActiveMetricQueryParams } from 'utils/metricQueryParams'

interface Props {
  keyMetric?: TKeyMetric
  metric: TCustomizedMetric | null
  onClose: () => void
  open: boolean
}

const MetricViewDialog = (props: Props) => {
  const { keyMetric, metric, onClose, open } = props

  const isMobile = useIsMobile()
  const { tryGetAllComments } = useCommentActions()

  useEffect(() => {
    if (keyMetric?.id) {
      tryGetAllComments(keyMetric.id)
    }
  }, [keyMetric?.id])

  const fullMetric = useMetric(
    metric?.id,
    metric?.settings.variant_kpi_template_id
  )
  const commentThreads = useCommentThreads(keyMetric?.id)
  const { tasks } = useTasksFromKeyMetric(keyMetric?.id)

  const [queryParams, setQueryParams] = useMetricQueryParamsState()
  const [chart, setChart] = useState<'bar' | 'line'>('line')
  const selectedChart = queryParams.segment_by ? 'bar' : chart

  const [focusedCommentThreadId, setFocusedCommentThreadId] = useState<
    string | null
  >(null)
  const [newCommentThread, setNewCommentThread] =
    useState<TCommentThreadData | null>(null)

  if (!fullMetric || !metric) return null

  // click comment
  const handleClickComment = (commentId: string) => {
    setFocusedCommentThreadId(commentId)
  }

  const handleAddComment = (value: TChartClick) => {
    const existing = commentThreads.find(
      (thread) => thread.data.label === value.label
    )

    if (existing) {
      handleClickComment(existing.id)

      return
    }

    setFocusedCommentThreadId(null)
    setNewCommentThread({
      datasetLabel: value.datasetLabel ?? keyMetric?.title ?? '',
      label: value.label,
      value: value.value ?? 0
    })
  }

  const metricViewProps = {
    chart: selectedChart,
    focusedMarkerId: focusedCommentThreadId ?? undefined,
    onAddComment: handleAddComment,
    onClickComment: handleClickComment,
    queryParams,
    setQueryParams,
    tasks,
    TitleProps: {
      hide: true
    }
  }

  return (
    <SidebarDialog
      fullHeight={true}
      footer={
        <MetricQueryParamsFooter
          chart={selectedChart}
          id={metric.id}
          onChangeChart={setChart}
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          settings={metric.settings}
          sx={{ px: 3, py: 1 }}
        />
      }
      hero={
        hasActiveMetricQueryParams(queryParams) ? (
          <MetricActiveFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        ) : undefined
      }
      maxWidth="lg"
      onClose={onClose}
      open={open}
      rightSidebar={
        keyMetric
          ? {
              content: (
                <KeyMetricSidebar
                  focusedCommentThreadId={focusedCommentThreadId}
                  id={keyMetric.id}
                  newCommentThread={newCommentThread}
                  setFocusedCommentThreadId={setFocusedCommentThreadId}
                  setNewCommentThread={setNewCommentThread}
                />
              )
            }
          : undefined
      }
      title={
        <MetricTitle
          context={Context.FROM_METRIC_PREVIEW_DIALOG}
          id={metric.id}
          link={!isMobile}
          keyMetric={keyMetric}
          settings={metric.settings}
          size={isMobile ? 'small' : undefined}
        />
      }
      titleActions={
        <IconButton onClick={onClose} size="small">
          <Icon fontSize="small" name="Close" />
        </IconButton>
      }
    >
      {keyMetric ? (
        <MetricView keyMetric={keyMetric} {...metricViewProps} />
      ) : (
        <MetricView
          clickOptions={{
            breakdownBy: true,
            explore: true
          }}
          id={metric.id}
          settings={metric.settings}
          {...metricViewProps}
        />
      )}
    </SidebarDialog>
  )
}

export default MetricViewDialog
